import axios from "axios";

export const fetchProductReviewsApi = async (productId, signal) => {
  // fetches the reviews for the product
  // productId (String): the id of the product whose reviews are to be fetched
  // signal (AbortSignal): the AbortSignal used to cancel the API request if needed
  try {
    const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/api/reviews/product/${productId}`, {
      signal,
    });
    return data;
  } catch (error) {
    //status code not in 200s
    if (!(error.name === "AbortError" || error.name === "CanceledError")) {
      // custom error message from back end
      const newErr = new Error(error.response?.data?.message);
      newErr.status = error.response?.request?.status;
      throw newErr;
    }
  }
};

export const fetchUserReviewsApi = async (
  token,
  signal,
  sortKey = null,
  filterKey = null,
  pageNr = null
) => {
  // fetches all reviews of a specified user
  // token (String): bearer token used for authentication
  // signal (AbortSignal): the AbortSignal used to cancel the API request if needed
  // sortKey (String): the attribute to sort on, eg "date_1"
  // filterKey (String): the attribute to filter on, eg "star_4"
  // pageNr (Number): the new page nr requested by the user (pagination)
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };

    let queryParams = "";
    if (pageNr) {
      queryParams = `?pageNr=${pageNr}`;
    }
    if (sortKey) {
      queryParams = queryParams ? queryParams + "&" : "?";
      queryParams = queryParams + `sortKey=${sortKey}`;
    }
    if (filterKey) {
      queryParams = queryParams ? queryParams + "&" : "?";
      queryParams = queryParams + `filterKey=${filterKey}`;
    }
    const { data } = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/reviews/user${queryParams}`,
      config,
      { signal }
    );
    return data;
  } catch (error) {
    //status code not in 200s
    if (!(error.name === "AbortError" || error.name === "CanceledError")) {
      // custom error message from back end
      const newErr = new Error(error.response?.data?.message);
      newErr.status = error.response?.request?.status;
      throw newErr;
    }
  }
};

export const adminFetchUserReviewsApi = async (
  token,
  userId,
  signal,
  pageNr = null
) => {
  try {
    // fetches all reviews of a specified user
    // token (String): bearer token used for authentication
    // userId (String): the id of the user whose reviews are to be fetched
    // signal (AbortSignal): the AbortSignal used to cancel the API request if needed
    // pageNr (Number): the new page nr requested by the user (pagination)
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    let queryParams = "";
    if (pageNr) {
      queryParams = `?pageNr=${pageNr}`;
    }
    const { data } = await axios.get(
      `${process.env.REACT_APP_API_URL}/api/reviews/admin/user/${userId}${queryParams}`,
      config,
      { signal }
    );
    return data;
  } catch (error) {
    //status code not in 200s
    if (!(error.name === "AbortError" || error.name === "CanceledError")) {
      // custom error message from back end
      const newErr = new Error(error.response?.data?.message);
      newErr.status = error.response?.request?.status;
      throw newErr;
    }
  }
};

export const createProductReviewApi = async (token, productId, reviewData) => {
  // post request to the server to createa  new review
  // token (String): bearer token used for authentication
  // productId (String): the id of the product the review is for
  // reviewData (JSON): object with the data for the review { title (String), comment (String), rating (Number)}
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const { data } = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/reviews/product/${productId}`,
      reviewData,
      config
    );
    return data;
  } catch (error) {
    //status code not in 200s
    if (!(error.name === "AbortError" || error.name === "CanceledError")) {
      // custom error message from back end
      const newErr = new Error(error.response?.data?.message);
      newErr.status = error.response?.request?.status;
      throw newErr;
    }
  }
};

export const editProductReviewApi = async (token, reviewData) => {
  // put request to the server to update a product review
  // token (String): bearer token used for authentication
  // reviewData (JSON): object with the updated data { title (String), comment (String), rating (Number)}
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const { data } = await axios.put(
      `${process.env.REACT_APP_API_URL}/api/reviews/${reviewData._id}`,
      reviewData,
      config
    );
    return data;
  } catch (error) {
    //status code not in 200s
    if (!(error.name === "AbortError" || error.name === "CanceledError")) {
      // custom error message from back end
      const newErr = new Error(error.response?.data?.message);
      newErr.status = error.response?.request?.status;
      throw newErr;
    }
  }
};

export const deleteProductReviewApi = async (token, reviewId) => {
  // delete request to the server to delete a product review
  // token (String): bearer token used for authentication
  // reviewId (String): the id of the review to be deleted
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const { data } = await axios.delete(`${process.env.REACT_APP_API_URL}/api/reviews/${reviewId}`, config);
    return data;
  } catch (error) {
    //status code not in 200s
    if (!(error.name === "AbortError" || error.name === "CanceledError")) {
      // custom error message from back end
      const newErr = new Error(error.response?.data?.message);
      newErr.status = error.response?.request?.status;
      throw newErr;
    }
  }
};

export const adminDeleteProductReviewApi = async (token, reviewId) => {
  // delete request to the server to delete a product review
  // token (String): bearer token used for authentication
  // reviewId (String): the id of the review to be deleted
  try {
    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const { data } = await axios.delete(
      `${process.env.REACT_APP_API_URL}/api/reviews/admin/${reviewId}`,
      config
    );
    return data;
  } catch (error) {
    //status code not in 200s
    if (!(error.name === "AbortError" || error.name === "CanceledError")) {
      // custom error message from back end
      const newErr = new Error(error.response?.data?.message);
      newErr.status = error.response?.request?.status;
      throw newErr;
    }
  }
};
